import * as React from "react";
import {
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Box,
  Popover,
  TextField,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { DateRange, DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CustomGenericModal from "./CustomGenericModal";

import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../../features/viewOrder/ViewOrderUtils";

export interface RangeConfig {
  label: string;
  days: number;
}
export const defaultRangesConfig: RangeConfig[] = [
    { label: "Today", days: 0 },
    { label: "Past Week", days: 7 },
    { label: "Past Month", days: 31 },
  ];

interface QuickDateRangePickerProps {
  handleStartDateChange: (range: [Dayjs, Dayjs]) => void;
  startDate?: Dayjs;
  endDate?: Dayjs;
  setDateRangeLabel?: React.Dispatch<React.SetStateAction<string>>;
}

export default function QuickDateRangePicker({
  handleStartDateChange,
  startDate,
  endDate,
  setDateRangeLabel
}: QuickDateRangePickerProps) {
  
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );

  const validDateRange: number = ConfigDictionary.dateRangeConfig ?? 31;

  const rangesConfig: RangeConfig[] = ConfigDictionary.quickDateRanges ?? defaultRangesConfig;

  const validStartDate = dayjs().subtract(validDateRange, "day");
  const [genericModalProps, setGenericModalProps] = React.useState<GenericModal>(initialGenericModalProps);
  const resetGenericModalProps = () => setGenericModalProps(initialGenericModalProps);

  const ranges = React.useMemo(
    () =>
      rangesConfig.map(({ label, days }) => ({
        label,
        days,
        getRange: (): [Dayjs, Dayjs] => {
          if (days === 0) return [dayjs().startOf("day"), dayjs().endOf("day")];
          return [dayjs().subtract(days, "day").startOf("day"), dayjs().endOf("day")];
        },
      })),
    []
  );

  const value = React.useRef<string>("0")
  const [customRange, setCustomRange] = React.useState<DateRange<Dayjs>>([null, null]);
  const [openPopover, setOpenPopover] = React.useState(false);

  const selectRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (startDate && endDate) {
      const match = ranges.find((r) => {
        const [rs, re] = r.getRange();
        return startDate.isSame(rs, "day") && endDate.isSame(re, "day");
      });
      if (match) {
        value.current = String(match.days)
      } else {
        value.current = "custom"
        setCustomRange([startDate, endDate]);
      }
    }
  }, [startDate, endDate, ranges]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selected = event.target.value;
    const label = selected === "custom" ? `selected Date Range`
        : (ranges.find(r => String(r.days) === selected)?.label || "Select Range");
    setDateRangeLabel?.(label)

    if (selected === "custom") {
      value.current="custom"
      setOpenPopover(true);
      return;
    }

    const selectedDays = Number(selected);
    const match = ranges.find((r) => r.days === selectedDays);
    if (match) {
      value.current = String(match.days);
      handleStartDateChange(match.getRange());
      setCustomRange([null, null]);
      setOpenPopover(false);
    }
  };

  const handleClosePopover = () => setOpenPopover(false);

  const renderValue = (selected: string) => {
    if (selected === "custom" && customRange[0] && customRange[1]) {
      return `${customRange[0].format("DD/MM/YYYY")} - ${customRange[1].format(
        "DD/MM/YYYY"
      )}`;
    }
    const match = ranges.find((r) => String(r.days) === selected);
    return match ? match.label : "Select Range";
  };

  return (
    <FormControl
      ref={selectRef}
      fullWidth
      sx={{ width: "180px", backgroundColor: "#FBFBFB", borderRadius: "12px" }}
    >
      <Select
        value={value.current}
        onChange={handleChange}
        
        renderValue={renderValue}
        IconComponent={(props) => <KeyboardArrowDownIcon {...props} sx={{ fontSize: "14px" }} />}
        sx={{
          fontSize: "12px",
          "& .MuiSelect-select": { py: 1.5 },
          "&.MuiOutlinedInput-root": {
            borderRadius: "8px",
            border: "1px solid #EBEBEB",
            "& fieldset": { borderColor: "transparent" },
            "&:hover fieldset": { borderColor: "transparent" },
            "&.Mui-focused fieldset": { borderColor: "transparent" },
          },
          height: 36,
        }}
      >
        {ranges.map((r, index) => (
          <MenuItem
            key={r.days}
            value={String(r.days)}
            sx={{
              fontSize: "12px",
              color: "#606060",
              fontFamily: "Poppins",
              padding: "8px 16px",
              borderBottom: index !== ranges.length - 1 ? "1px dashed #EBEBEB" : "none",
              margin: "0 7px",
              listStyle: "none",
              cursor: "pointer",
              borderRadius: "6px",
              transition: "background 0.2s",
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            {r.label}
          </MenuItem>
        ))}

        <MenuItem
          value="custom"
          onClick={() => {
            if (value.current === "custom") {
              setCustomRange([null, null]);
              setOpenPopover(true)
            };
          }}
          sx={{
            fontSize: "12px",
            color: "#606060",
            fontFamily: "Poppins",
            padding: "8px 16px",
            margin: "0 7px",
            listStyle: "none",
            cursor: "pointer",
            borderRadius: "6px",
            transition: "background 0.2s",
            "&:hover": { backgroundColor: "#F5F5F5" },
          }}
        >
          Custom Range
        </MenuItem>
      </Select>
      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        rightButtonText={genericModalProps?.rightButtonText}
        modalOpen={genericModalProps?.open}
        onLeftButtonClick={genericModalProps?.onLeftButtonClick}
        onRightButtonClick={genericModalProps?.onRightButtonClick}
        leftButtonPadding={genericModalProps?.leftButtonPadding}
        onClose={resetGenericModalProps}
        width={genericModalProps?.width}
      />
      <Popover
        open={openPopover}
        anchorEl={selectRef.current}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateRangePicker
              value={customRange}
              maxDate={dayjs()} // restrict future dates
              onChange={(newValue) => {
                if (newValue[0] && newValue[1]) {
                  const diff = newValue[1].diff(newValue[0], "day");
                  if (diff > validDateRange) {
                    setGenericModalProps(
                      getGenericModalProps(
                        "positive",
                        `You can't select date range more than ${validDateRange} days!`
                      )
                    );
                    return;
                  }
                  setCustomRange(newValue);
                  handleStartDateChange([newValue[0], newValue[1]]);
                  setOpenPopover(false);
                } else {
                  setCustomRange(newValue);
                }
              }}
              renderInput={(startProps, endProps) => (
                <div style={{ display: "none" }}>
                  <input {...startProps.inputProps} />
                  <input {...endProps.inputProps} />
                </div>
              )}
              calendars={2} // directly show two calendars
              open
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </FormControl>
  );
}
